import {
  KnowledgeSpace,
  KnowledgeSpaceDetails,
} from '../data/schema/knowledgeSpacesSchema'
import { toast } from 'sonner'
/**
 * Mock data for Knowledge Spaces
 */
const mockSpaces: Record<string, KnowledgeSpaceDetails> = {
  'space-ng-partner': {
    id: 'space-ng-partner',
    name: 'NG Partner View',
    visibility: 'Partner',
    description:
      'External-facing Knowledge Space for National Geographic partners to access ordering, status tracking, and approved templates.',
    documentCount: 47,
    lastUpdated: '2025-11-01T10:00:00Z',
    boundBots: ['Quad Assist'],
    accessLevel: 'read',
    documents: [
      {
        id: 'doc-1',
        title: 'Order Form Template',
        type: 'PDF',
        size: 2400000,
        uploadedAt: '2025-10-15T14:30:00Z',
        uploadedBy: 'Merritt Lane',
      },
      {
        id: 'doc-2',
        title: 'Brand Guidelines',
        type: 'PDF',
        size: 5600000,
        uploadedAt: '2025-09-20T09:15:00Z',
        uploadedBy: 'Ava Brooks',
      },
    ],
    collaborators: [
      {
        id: 'u2',
        name: 'Merritt Lane',
        role: 'Account Admin',
        accessLevel: 'admin',
      },
      {
        id: 'u3',
        name: 'Ava Brooks',
        role: 'Account Member',
        accessLevel: 'write',
      },
    ],
    settings: {
      allowExternalAccess: true,
      requireApproval: false,
      retentionDays: 365,
    },
  },
  'space-ng-core': {
    id: 'space-ng-core',
    name: 'NG Account Core',
    visibility: 'Internal',
    description:
      'Internal Knowledge Space containing pricing guardrails, plant capabilities, specs, and operational rules for National Geographic.',
    documentCount: 89,
    lastUpdated: '2025-11-01T10:00:00Z',
    boundBots: ['QuadChat Assistant'],
    accessLevel: 'write',
    documents: [
      {
        id: 'doc-3',
        title: 'Pricing Matrix 2025',
        type: 'Excel',
        size: 1200000,
        uploadedAt: '2025-10-01T08:00:00Z',
        uploadedBy: 'Merritt Lane',
      },
      {
        id: 'doc-4',
        title: 'Plant Capabilities Overview',
        type: 'PDF',
        size: 3400000,
        uploadedAt: '2025-09-15T11:45:00Z',
        uploadedBy: 'Liam Chen',
      },
    ],
    collaborators: [
      {
        id: 'u2',
        name: 'Merritt Lane',
        role: 'Account Admin',
        accessLevel: 'admin',
      },
      {
        id: 'u4',
        name: 'Liam Chen',
        role: 'Account Member',
        accessLevel: 'write',
      },
    ],
    settings: {
      allowExternalAccess: false,
      requireApproval: true,
      retentionDays: 730,
    },
  },
}
/**
 * Lists all Knowledge Spaces for a given account.
 * In production, this would query the Sprinklenet Knowledge Spaces API.
 */
export async function listKnowledgeSpaces(
  accountId: string,
): Promise<KnowledgeSpace[]> {
  try {
    await new Promise((resolve) => setTimeout(resolve, 600))
    console.log(
      `[Spaces Client] Listing Knowledge Spaces for account ${accountId}...`,
    )
    // Simulate occasional failures
    if (Math.random() < 0.01) {
      throw new Error('Spaces API unavailable')
    }
    // Return mock spaces based on account
    const spaces = Object.values(mockSpaces).map(
      ({ documents, collaborators, settings, ...space }) => space,
    )
    return spaces
  } catch (error) {
    console.error(
      `[Spaces Client] Failed to list Knowledge Spaces for account ${accountId}:`,
      error,
    )
    toast.error('Unable to load Knowledge Spaces', {
      description: 'Unable to load data at this time. Please try again.',
      duration: 4000,
    })
    return []
  }
}
/**
 * Gets detailed information about a specific Knowledge Space.
 * In production, this would fetch from Sprinklenet Knowledge Spaces API.
 */
export async function getSpaceDetails(
  spaceId: string,
): Promise<KnowledgeSpaceDetails | null> {
  try {
    await new Promise((resolve) => setTimeout(resolve, 500))
    console.log(`[Spaces Client] Fetching details for space ${spaceId}...`)
    // Simulate occasional failures
    if (Math.random() < 0.01) {
      throw new Error('Space details unavailable')
    }
    const space = mockSpaces[spaceId]
    if (!space) {
      console.warn(`[Spaces Client] Space ${spaceId} not found`)
      toast.warning('Space not found', {
        description: 'The requested Knowledge Space could not be found.',
        duration: 3000,
      })
      return null
    }
    return space
  } catch (error) {
    console.error(
      `[Spaces Client] Failed to fetch details for space ${spaceId}:`,
      error,
    )
    toast.error('Unable to load space details', {
      description: 'Unable to load data at this time. Please try again.',
      duration: 4000,
    })
    return null
  }
}
/**
 * Syncs an account's data with all bound Knowledge Spaces.
 * In production, this would trigger a sync job in Sprinklenet.
 */
export async function syncAccountSpaces(accountId: string): Promise<boolean> {
  try {
    await new Promise((resolve) => setTimeout(resolve, 1500))
    console.log(
      `[Spaces Client] Syncing all Knowledge Spaces for account ${accountId}...`,
    )
    // Simulate occasional sync failures
    if (Math.random() < 0.01) {
      throw new Error('Sync service unavailable')
    }
    console.log(
      '[Spaces Client] Sync complete. Updated 2 spaces, 137 documents.',
    )
    return true
  } catch (error) {
    console.error(
      `[Spaces Client] Failed to sync spaces for account ${accountId}:`,
      error,
    )
    toast.error('Sync failed', {
      description: 'Unable to sync Knowledge Spaces at this time.',
      duration: 4000,
    })
    return false
  }
}
